// ui/trick-sequencer.js: #bug4 trick-completion orchestrator.
//
// THE PROBLEM THIS SOLVES
// -----------------------
// The server resolves a trick inside the same `play_card` call that records
// the 4th card: `state.lua` does `completed_tricks[#+1] = {...}` and then
// immediately `self._public.trick_plays = {}` (state.lua ~L433-438). So both
// the 4th `CARD_PLAYED` event AND the `TRICK_RESOLVED` event that follow it
// carry a snapshot where `trick_plays` is ALREADY empty and the trick already
// lives in `completed_tricks`.
//
// Consequences before this module existed:
//   (a) the 4th card never animated in, useCardPlayAnimation watches
//       `trick_plays` growing, but it goes 3 → 0, so the 4th play is never
//       detected → the card just snaps away with the rest.
//   (b) the four cards vanished with no sweep, NetworkedGameView rendered the
//       trick area with `trick_resolution: null` and a now-empty `trick_plays`,
//       so the cards disappeared instead of sweeping toward the winner.
//
// WHAT THIS DOES
// --------------
// A pure state machine fed one snapshot at a time. When it sees
// `completed_tricks` grow, it reconstructs the just-finished trick (its four
// plays + winner_seat both live in `completed_tricks[last]`) and drives a
// timed sequence so the renderer can show the whole moment:
//
//   PLAY_IN (slide the 4th card in) → HOLD (all four shown) → SWEEP (cards arc
//   to the winner's nameplate + flash) → done (release to the live snapshot).
//
// Each frame `frameAt(nowMs)` returns an *effective* render state, the
// `trick_plays` to draw (reconstructed so the four cards stay on screen during
// the window), the `slide` overlay for the 4th card, and the `trick_resolution`
// overlay for the sweep, all consumed by canvas/trick-area.js, which already
// knows how to draw both `slide` and `trick_resolution`.
//
// Reduced motion: collapses to a short hold then an instant clear (no slide,
// no sweep), matches the rest of the game's reduced-motion contract.
//
// Interruptions: any snapshot that moves the game on (next trick's first card, a
// new hand, GAME_INITIALIZED / reconnect) preempts an in-flight sequence
// cleanly. The machine never blocks the live snapshot from rendering, once
// the window elapses (or is preempted) `frameAt` returns null and the caller
// falls back to the raw snapshot.

import { easeOut } from './easing.js';

// Phase durations (ms), the restyle "Snappy v2" pace from the chosen
// mockup (docs/design-2026-10/restyle-cream/shared/app.js resolve/sweep).
// A bot that leads the next trick waits for this whole sequence first (the
// server's TRICK_CLEAR_PAD_MS equals SEQUENCE_MS, pinned by a server test),
// so it never preempts mid-sweep; a human on lead cannot play until it
// ends either (NetworkedGameView locks the hand while a frame is out).
const PLAY_IN_MS = 100;   // 4th card slides from its seat into the trick slot
const HOLD_MS    = 260;   // all four cards held so the player reads the trick
const SWEEP_MS   = 130;   // cards arc toward the winner's nameplate and fade
// No separate flash phase: the winner flash (the mockup's 120ms pop,
// canvas/trick-area.js FLASH_DURATION_MS) runs inside the sweep, so the
// time it adds after the sweep is 0.
const FLASH_MS   = 0;
// Reduced motion: brief hold so the four cards register, then instant clear.
const REDUCED_HOLD_MS = 220;

// 100 + 260 + 130 + 0 = 490ms from the 4th card's play to the clear.
const SEQUENCE_MS = PLAY_IN_MS + HOLD_MS + SWEEP_MS + FLASH_MS;

/**
 * @typedef {{ seat: number, card_id: string }} Play
 */

/**
 * Normalize a Lua-table-as-object (or array) of plays into a real array of
 * `{seat, card_id}`. parity.serialize emits every Lua table as a JSON object
 * keyed by stringified integers, so callers may hand us either shape.
 *
 * @param {any} raw
 * @returns {Play[]}
 */
function normalizePlays(raw) {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : Object.values(raw);
  return list
    .filter((p) => p && p.card_id != null && p.seat != null)
    .map((p) => ({ seat: Number(p.seat), card_id: String(p.card_id) }));
}

/**
 * Pull the completed-tricks array out of a snapshot in normalized form.
 *
 * @param {any} snapshot
 * @returns {Array<{winner_seat:number, plays:Play[]}>}
 */
function normalizeCompletedTricks(snapshot) {
  const raw = snapshot?.completed_tricks;
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : Object.values(raw);
  return list.map((t) => ({
    winner_seat: Number(t?.winner_seat),
    plays: normalizePlays(t?.plays),
  }));
}

/**
 * The card_id played by the seat that moved last in this trick, i.e. the
 * card whose play completed the trick. We treat the LAST entry of the trick's
 * `plays` as the finishing card; the engine appends plays in turn order, so
 * the final entry is always the 4th (or 3rd, when a player went alone) card.
 *
 * @param {Play[]} plays
 * @returns {Play | null}
 */
function finishingPlay(plays) {
  if (!plays || plays.length === 0) return null;
  return plays[plays.length - 1];
}

/**
 * Build the trick-resolution sequencer. One instance per game mount (same
 * lifecycle as createTrickResolutionDetector / createCardPlayDetector).
 *
 * @param {{ reducedMotion?: boolean }} [opts]
 */
export function createTrickSequencer(opts = {}) {
  const reducedMotion = !!opts.reducedMotion;

  /** @type {number} */
  let completedHighWater = 0;
  /**
   * The active resolution sequence, or null when idle.
   * @type {null | {
   *   plays: Play[],
   *   winner_seat: number,
   *   finishing: Play | null,
   *   startMs: number,
   *   reduced: boolean,
   * }}
   */
  let active = null;

  /**
   * Feed the latest snapshot. Returns true if a NEW trick resolution was
   * detected (and a sequence started), false otherwise. The caller then polls
   * frameAt(nowMs) each animation frame for the render state.
   *
   * @param {any} snapshot
   * @param {number} nowMs
   * @returns {boolean}
   */
  function observe(snapshot, nowMs) {
    if (!snapshot) return false;
    const completed = normalizeCompletedTricks(snapshot);

    // Hand reset (or reconnect): completed_tricks shrank. Drop any in-flight
    // sequence and re-baseline so we never replay an old trick.
    if (completed.length < completedHighWater) {
      completedHighWater = completed.length;
      active = null;
      return false;
    }

    // No new trick resolved since last time.
    if (completed.length === completedHighWater) {
      // A later snapshot for the SAME completed count (e.g. the next trick's
      // first CARD_PLAYED, which has trick_plays growing again) means the game
      // moved on. Preempt any in-flight sweep so the live trick renders.
      const livePlays = normalizePlays(snapshot.trick_plays);
      if (active && livePlays.length > 0) active = null;
      return false;
    }

    // One (or more) tricks resolved. Animate the most recent one.
    const resolved = completed[completed.length - 1];
    completedHighWater = completed.length;
    if (!resolved || !Array.isArray(resolved.plays) || resolved.plays.length === 0) {
      active = null;
      return false;
    }
    active = {
      plays: resolved.plays,
      winner_seat: resolved.winner_seat,
      finishing: finishingPlay(resolved.plays),
      startMs: nowMs,
      reduced: reducedMotion,
    };
    return true;
  }

  /**
   * The render state for the current frame, or null when idle / finished.
   *
   * Shape (consumed by NetworkedGameView → renderTrickArea):
   *   {
   *     trick_plays: Play[],         // reconstructed cards to draw
   *     slide: {seat, card_id, t}|null,        // 4th-card play-in overlay
   *     trick_resolution: {winner_seat, elapsed_ms, reduced_motion}|null,
   *     done: boolean,               // sequence elapsed, caller may release
   *   }
   *
   * @param {number} nowMs
   * @returns {null | {
   *   trick_plays: Play[],
   *   slide: {seat:number, card_id:string, t:number} | null,
   *   trick_resolution: {winner_seat:number, elapsed_ms:number, reduced_motion:boolean} | null,
   *   done: boolean,
   * }}
   */
  function frameAt(nowMs) {
    if (!active) return null;
    const elapsed = Math.max(0, nowMs - active.startMs);

    if (active.reduced) {
      // Reduced motion: hold the four cards briefly, then clear. No slide,
      // no sweep, the four cards just appear and disappear.
      if (elapsed >= REDUCED_HOLD_MS) {
        active = null;
        return null;
      }
      return {
        trick_plays: active.plays,
        slide: null,
        trick_resolution: null,
        done: false,
      };
    }

    if (elapsed >= SEQUENCE_MS) {
      active = null;
      return null;
    }

    // Phase 1, PLAY_IN: the finishing card slides in; the others sit static.
    if (elapsed < PLAY_IN_MS && active.finishing) {
      const t = easeOut(Math.min(1, elapsed / PLAY_IN_MS));
      return {
        trick_plays: active.plays,
        slide: {
          seat: active.finishing.seat,
          card_id: active.finishing.card_id,
          t,
        },
        trick_resolution: null,
        done: false,
      };
    }

    // Phase 2, HOLD: all four cards static, no overlay.
    if (elapsed < PLAY_IN_MS + HOLD_MS) {
      return {
        trick_plays: active.plays,
        slide: null,
        trick_resolution: null,
        done: false,
      };
    }

    // Phase 3, SWEEP with the FLASH inside it: cards arc toward the winner. We feed
    // trick-area.js a `trick_resolution` whose elapsed_ms is measured from the
    // start of the sweep so its own SWEEP/FLASH split lines up.
    const sweepElapsed = elapsed - (PLAY_IN_MS + HOLD_MS);
    return {
      trick_plays: active.plays,
      slide: null,
      trick_resolution: {
        winner_seat: active.winner_seat,
        elapsed_ms: sweepElapsed,
        reduced_motion: false,
      },
      done: false,
    };
  }

  /** True while a sequence is in flight, caller keeps the rAF loop alive. */
  function isActive() {
    return active !== null;
  }

  /** Force-clear any in-flight sequence (unmount / hard reset). */
  function reset() {
    active = null;
    completedHighWater = 0;
  }

  return { observe, frameAt, isActive, reset };
}

export const _internals = {
  PLAY_IN_MS,
  HOLD_MS,
  SWEEP_MS,
  FLASH_MS,
  REDUCED_HOLD_MS,
  SEQUENCE_MS,
  normalizePlays,
  normalizeCompletedTricks,
  finishingPlay,
};
